import React, { useState, useEffect, useMemo, use } from "react";
import RPMTorqueSelector from "./RPMTorqueSelector";
import EfficiencyBarChart from "./EfficiencyBarChart";
import EfficiencyMatrixPlot from "./EfficiencyMatrixPlot";
import Footer from "./Footer";
import Header from "./Header";
import { Typography } from "@mui/material";
import "../App.css";
import TableDataContainer from "./TableDataContainer";
import FilterDescription from "./FilterDescription";
import { Box } from "@mui/material";
import FlowPressureSelector from "./FlowPressureSelector";
import {
  getSamplePoints,
  getEfficiencyMatrix,
  motorEfficiencyWithParams,
} from "../api/index";

const ScatterGraph = () => {
  let motorCounts;
  const [table, setTable] = useState({});
  const [tableData, setTableData] = useState([]);
  const [rowselected, setRowSelected] = useState(false);
  const [filterSelected, setFilterSelected] = useState(false);
  const [samplePoints, setSamplePoints] = useState([]);
  const [isTransferEnabled, setIsTransferEnabled] = useState(false); // Track button enable state
  const [layout, setLayout] = useState("table");
  const [selectedMeasurement, setSelectedMeasurement] = useState("Metric"); // State to track selected measurement
  const [page, setPage] = useState(0); // Current page
  const [rowsPerPage, setRowsPerPage] = useState(10); // Rows per page
  const [matches, setMatches] = useState([]);
  const [order, setOrder] = useState("asc"); // Sorting order
  const [orderBy, setOrderBy] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [filterSelectionHistory, setFilterSelectionHistory] = useState([]);
  const [selectedToggle, setSelectedToggle] = useState("TorqueSpeed");
  const [selectedFilters, setSelectedFilters] = useState({
    radialBearingLoad: "",
    gearwheelSet: "",
    valveSystem: "",
  });
  const [selectedRows, setSelectedRows] = useState([]); // State to track selected rows
  const [selectedBarData, setSelectedBarData] = useState(null);
  const [loader, setLoader] = useState(true);
  const [bearingCapacityValues, setbearingCapacityValues] = useState([]);
  const [userSelectedFromCheckbox, setuserSelectedFromCheckbox] =
    useState(false);
  const [barLoader, setBarLoader] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);
  const [tableFilter, setTableFilter] = useState({});
  const [barError, setBarError] = useState(false);
  const [iBarError, setiBarError] = useState(false);
  const [hover, setHover] = useState(false);
  const [convertedValues, setConvertedValues] = useState([]);
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Series",
        field: "series",
      },
      {
        headerName: "Displacement",
        field: "displacement",
      },
      { headerName: "Quick selector link", field: "dummyField" },
      {
        headerName: "Pressure",
        field: "pressure",
      },
      {
        headerName: "Flow",
        field: "flow",
      },
      {
        headerName: "Valve type",
        field: "valveType",
      },
      {
        headerName: "Gearset type",
        field: "gearsetType",
      },
      {
        headerName: "Bearing Type",
        field: "bearingType",
      },
      {
        headerName: "Bearing Capacity",
        field: "bearingCapacity",
      },
      // {
      //   headerName: "TPI (%)",
      //   field: "durabilityIndex",
      // },
      {
        headerName: "Eff. (%)",
        field: "efficiency",
      },
    ],
    []
  );
  const findActualValues = async (x, y, selectedToggle, combinedValues) => {
    if (!combinedValues || combinedValues.length === 0) return null;
    switch (selectedToggle) {
      case "TorqueSpeed": {
        const match = combinedValues.find(
          (val) =>
            Number(val.imperialTorque) === Number(y) &&
            Number(val.imperialSpeed) === Number(x)
        );
        if (match) {
          return {
            actualTorque: match.actualTorque,
            actualSpeed: match.actualSpeed,
          };
        }
        return null;
      }

      case "FlowPressure": {
        const match = combinedValues.find(
          (val) =>
            Number(val.imperialFlow) === Number(x) &&
            Number(val.imperialPressure) === Number(y)
        );
        if (match) {
          return {
            actualFlow: match.actualFlow,
            actualPressure: match.actualPressure,
          };
        }
        return null;
      }

      default:
        return null;
    }
  };

  const handleSelection = async ({ x: rpm, y: torque }) => {
    let requestBody;
    if (layout === "bar") setBarLoader(true);
    if (layout === "table") setTableLoader(true);
    setSelectedRows([]);
    setSelectedBarData(null);
    setIsTransferEnabled(false);
    setRowSelected(false);
    if (
      selectedToggle === "TorqueSpeed" &&
      selectedMeasurement === "Imperial"
    ) {
      const actualMatch = await findActualValues(
        rpm,
        torque,
        selectedToggle,
        convertedValues
      );
      requestBody = JSON.stringify({
        torque: actualMatch ? actualMatch.actualTorque : torque,
        speed: actualMatch ? actualMatch.actualSpeed : rpm,
        pressure: null,
        flow: null,
        number_of_motors: 174,
        minimum_efficiency: 0,
        unit_type: "OrbitalMotor",
        viscosity: 0,
        measurementUnit: selectedMeasurement,
        selectedToggle: selectedToggle,
      });
    } else {
      requestBody = JSON.stringify({
        torque: torque,
        speed: rpm,
        pressure: null,
        flow: null,
        number_of_motors: 174,
        minimum_efficiency: 0,
        unit_type: "OrbitalMotor",
        viscosity: 0,
        measurementUnit: selectedMeasurement,
        selectedToggle: selectedToggle,
      });
    }
    try {
      const res = await motorEfficiencyWithParams({
        data: requestBody,
      });
      if (res) {
        setBarError(false);
      }
      const efficiencyData = res.data.efficiency;
      function extractDurabilityIndexes(efficiencyData) {
        return efficiencyData.map((motor) => {
          const durabilityObj = motor.characteristics.find(
            (char) => char.name === "Durability Index"
          );
          const durabilityValue = durabilityObj
            ? parseFloat(durabilityObj.value)
            : 0;
          const durabilityPercent = durabilityValue;
          const gearWheelSet = motor.characteristics.find(
            (char) => char.name === "Gearset Type"
          );
          const valveSystem = motor.characteristics.find(
            (char) => char.name === "Valve Type"
          );
          const radialBearingLoad = motor.characteristics.find(
            (char) => char.name === "Bearing Capacity"
          );

          return {
            name: `${motor.series} ${motor.displacement}`,
            efficiency: motor.overall_efficiency,
            durabilityPercent: durabilityPercent.toFixed(2) + "%",
            gearWheelSet: gearWheelSet.value,
            valveSystem: valveSystem.value,
            radialBearingLoad: radialBearingLoad.value,
          };
        });
      }
      const durabilityData = extractDurabilityIndexes(efficiencyData);
      function extractMotorInfo(data) {
        return res.data.efficiency.map((motor) => {
          const getCharacteristic = (name) =>
            motor.characteristics.find((c) => c.name === name)?.value || null;

          return {
            series: motor.series,
            displacement: motor.displacement,
            pressure: motor.pressure,
            flow: motor.flow,
            valveType: getCharacteristic("Valve Type"),
            gearsetType: getCharacteristic("Gearset Type"),
            bearingType: getCharacteristic("Bearing Type"),
            bearingCapacity: getCharacteristic("Bearing Capacity"),
            durabilityIndex: getCharacteristic("Durability Index"),
            efficiency: motor.overall_efficiency,
          };
        });
      }
      const tableDatainfo = extractMotorInfo(res.data.efficiency);
      setTableData(tableDatainfo);
      setMatches(durabilityData);
      setBarLoader(false);
      setTableLoader(false);
    } catch (error) {
      setBarError(true);
      setBarLoader(false);
      setTableLoader(false);
    }
  };
  const handleSelectionForFlowAndPressure = async ({
    x: flow,
    y: pressure,
  }) => {
    let requestBody;
    setBarLoader(true);
    if (layout === "table") setTableLoader(true);
    setSelectedRows([]);
    setSelectedBarData(null);
    setIsTransferEnabled(false);
    setRowSelected(false);
    if (
      selectedToggle === "FlowPressure" &&
      selectedMeasurement === "Imperial"
    ) {
      const actualMatch = await findActualValues(
        flow,
        pressure,
        selectedToggle,
        convertedValues
      );
      requestBody = JSON.stringify({
        torque: null,
        speed: null,
        pressure: actualMatch ? actualMatch.actualPressure : pressure,
        flow: actualMatch ? actualMatch.actualFlow : flow,
        number_of_motors: 174,
        minimum_efficiency: 0,
        unit_type: "OrbitalMotor",
        viscosity: 0,
        measurementUnit: selectedMeasurement,
        selectedToggle: selectedToggle,
      });
    } else {
      requestBody = JSON.stringify({
        torque: null,
        speed: null,
        pressure: pressure,
        flow: flow,
        number_of_motors: 174,
        minimum_efficiency: 0,
        unit_type: "OrbitalMotor",
        viscosity: 0,
        measurementUnit: selectedMeasurement,
        selectedToggle: selectedToggle,
      });
    }
    try {
      const res = await motorEfficiencyWithParams({
        data: requestBody,
      });
      if (res) {
        setiBarError(false);
      }
      const efficiencyData = res.data.efficiency;
      function extractDurabilityIndexes(efficiencyData) {
        return efficiencyData.map((motor) => {
          const durabilityObj = motor.characteristics.find(
            (char) => char.name === "Durability Index"
          );
          const durabilityValue = durabilityObj
            ? parseFloat(durabilityObj.value)
            : 0;
          const durabilityPercent = durabilityValue;
          const gearWheelSet = motor.characteristics.find(
            (char) => char.name === "Gearset Type"
          );
          const valveSystem = motor.characteristics.find(
            (char) => char.name === "Valve Type"
          );
          const radialBearingLoad = motor.characteristics.find(
            (char) => char.name === "Bearing Capacity"
          );

          return {
            name: `${motor.series} ${motor.displacement}`,
            efficiency: motor.overall_efficiency,
            durabilityPercent: durabilityPercent.toFixed(2) + "%",
            gearWheelSet: gearWheelSet.value,
            valveSystem: valveSystem.value,
            radialBearingLoad: radialBearingLoad.value,
          };
        });
      }
      const durabilityData = extractDurabilityIndexes(efficiencyData);
      setTableLoader(true);
      function extractMotorInfo(data) {
        return res.data.efficiency.map((motor) => {
          const getCharacteristic = (name) =>
            motor.characteristics.find((c) => c.name === name)?.value || null;

          return {
            series: motor.series,
            displacement: motor.displacement,
            torque: motor.torque,
            speed: motor.speed,
            valveType: getCharacteristic("Valve Type"),
            gearsetType: getCharacteristic("Gearset Type"),
            bearingType: getCharacteristic("Bearing Type"),
            bearingCapacity: getCharacteristic("Bearing Capacity"),
            durabilityIndex: getCharacteristic("Durability Index"),
            efficiency: motor.overall_efficiency,
          };
        });
      }
      const tableDatainfo = extractMotorInfo(res.data.efficiency);
      setTableData(tableDatainfo);
      setMatches(durabilityData);
      setBarLoader(false);
      setTableLoader(false);
    } catch (error) {
      setiBarError(true);
      setBarLoader(false);
      setTableLoader(false);
    }
  };

  useEffect(() => {
    async function loadData() {
      setLoader(true);
      try {
        const lastSelection =
          filterSelectionHistory[filterSelectionHistory.length - 1];
        setTableFilter(lastSelection);
        const queryParams = {
          unit_type: "OrbitalMotor",
          viscosity: 0,
          sampleType: selectedToggle,
          measurementUnit: selectedMeasurement,
        };
        if (lastSelection?.radialBearingLoad)
          queryParams.radialBearingLoad = lastSelection.radialBearingLoad;

        if (lastSelection?.gearwheelSet)
          queryParams.gearwheelSet = lastSelection.gearwheelSet;

        if (lastSelection?.valveSystem)
          queryParams.valveSystem = lastSelection.valveSystem;
        const res = await getSamplePoints(queryParams);
        setbearingCapacityValues(res.data.bearingCapacityValues);
        setSamplePoints(res.data.samplePoints);
        setConvertedValues(res.data.combinedValues);
        setTable(res.data);
        setLoader(false);
      } catch (err) {
        console.error(err);
      }
    }
    loadData();
  }, [selectedToggle, selectedMeasurement, filterSelectionHistory]);

  useEffect(() => {
    async function fetchEfficiencyMatrix() {
      if (
        selectedMeasurement === "Metric" &&
        selectedToggle === "TorqueSpeed"
      ) {
        setLoader(true);
        await handleSelection({ x: 289, y: 1444 }); // Torque-Speed
        setLoader(false);
      } else if (
        selectedMeasurement === "Metric" &&
        selectedToggle === "FlowPressure"
      ) {
        setLoader(true);
        await handleSelectionForFlowAndPressure({ x: 125, y: 180 }); // Flow-Pressure
        setLoader(false);
      } else if (
        selectedMeasurement === "Imperial" &&
        selectedToggle === "TorqueSpeed"
      ) {
        await handleSelection({ x: 344, y: 1111 });
      } else if (
        selectedMeasurement === "Imperial" &&
        selectedToggle === "FlowPressure"
      ) {
        await handleSelectionForFlowAndPressure({ x: 95, y: 180 });
      }
    }
    fetchEfficiencyMatrix();
  }, [selectedMeasurement, selectedToggle]);

  const rpms = samplePoints
    .map((point) => point.speed)
    .filter((tq) => tq !== 0 && tq !== null && tq !== undefined);
  const torques = samplePoints
    .map((point) => point.torque)
    .filter((tq) => tq !== 0 && tq !== null && tq !== undefined);
  const pressure = samplePoints
    .map((point) => point.pressure)
    .filter((tq) => tq !== 0 && tq !== null && tq !== undefined);
  const flow = samplePoints
    .map((point) => point.flow)
    .filter((tq) => tq !== 0 && tq !== null && tq !== undefined);

  // 4) Compute motor‐counts heatmap
  motorCounts = useMemo(() => {
    const temp = {};
    if (selectedToggle === "TorqueSpeed") {
      samplePoints.forEach((row) => {
        const rpm = Math.round(row.speed);
        const tq = Math.round(row.torque);
        const key = `${rpm}-${tq}`;
        row.products.forEach((productId) => {
          const product = table.productCharacteristics.find(
            (p) => p.product_id === productId
          );
          if (product) {
            const combo = `${product.series}-${product.displacement}`;
            if (!temp[key]) temp[key] = new Set();
            temp[key].add(combo);
          }
        });
      });
    } else if (selectedToggle === "FlowPressure") {
      samplePoints.forEach((row) => {
        const flow = Math.round(row.flow);
        const pressure = Math.round(row.pressure);
        const key = `${flow}-${pressure}`;
        row.products.forEach((productId) => {
          const product = table.productCharacteristics.find(
            (p) => p.product_id === productId
          );
          if (product) {
            const combo = `${product.series}-${product.displacement}`;
            if (!temp[key]) temp[key] = new Set();
            temp[key].add(combo);
          }
        });
      });
    }
    return Object.fromEntries(
      Object.entries(temp).map(([k, set]) => [k, set.size])
    );
  }, [selectedToggle, samplePoints, table.productCharacteristics]);

  // 5) Handle point selection

  const toggleRow = (rowIndex) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilterSelected(true);
    const updatedFilters = {
      ...selectedFilters,
      [filterName]: value,
    };
    setSelectedFilters(updatedFilters);
    setFilterSelectionHistory((prevHistory) => [
      ...prevHistory,
      {
        radialBearingLoad: updatedFilters.radialBearingLoad,
        gearwheelSet: updatedFilters.gearwheelSet,
        valveSystem: updatedFilters.valveSystem,
      },
    ]);
  };

  const toggleRowSelection = async (row, isSelected) => {
    const rowData = {
      series: row.series,
      displacement: row.displacement,
    };

    setSelectedRows((prev) => {
      let updatedRows;

      if (isSelected) {
        if (
          prev.some(
            (r) =>
              r.series === rowData.series &&
              r.displacement === rowData.displacement
          )
        )
          return prev;

        if (prev.length >= 3) return prev;
        updatedRows = [...prev, rowData];
      } else {
        updatedRows = prev.filter(
          (r) =>
            !(
              r.series === rowData.series &&
              r.displacement === rowData.displacement
            )
        );
      }
      setuserSelectedFromCheckbox(true);
      setIsTransferEnabled(updatedRows.length > 0);
      setRowSelected(updatedRows.length > 0);
      return updatedRows;
    });
  };

  const handleBarClick = (data, index) => {
    setSelectedBarData(data); // Log selected bar data
    setIsTransferEnabled(true); // Enable the button when a bar is clicked
  };

  return (
    <div>
      <div>
        <Header />

        <div
          style={{
            width: "100%",
            margin: 0,
            padding: 0,
            boxSizing: "border-box",
          }}
        >
          {/* <Box
            sx={{
              height: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: "yellow",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              <Typography sx={{ fontSize: "14px", fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif` }}>
                Pre-release version, intended for testing and feedback.
              </Typography>
            </Box>
          </Box> */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "1500px", // Keeps it constrained and centered
              padding: "15px 20px 10px 20px",
              boxSizing: "border-box",
              margin: "0 auto", // Center it horizontally
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  color: "black",
                  marginBottom: "5px",
                  fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                }}
              >
                Fast and smart orbital motor pre-selection
              </Typography>
              <Box sx={{ paddingRight: "10px" }}>
                <Box>
                  <Typography
                    onClick={() =>
                      window.open(
                        "https://www.youtube.com/watch?v=ZepmmOCIlec",
                        "_blank"
                      )
                    }
                    variant="body1"
                    sx={{
                      color: "black",
                      textDecoration: "underline",
                      cursor: "pointer",

                      fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    How to use motor pre-selector
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: "#333",
                lineHeight: 1.6,
                fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
              }}
            >
              This tool facilitates the initial selection of hydraulic orbital
              motors based on key application parameters, including required
              torque, operating speed, pressure, and flow.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                marginTop: "5px",
                color: "#666",
                fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
              }}
            >
              Disclaimer: This tool provides preliminary recommendations only
              and is offered “as is” without warranties. Final selection must be
              verified through detailed technical evaluation. By using this
              tool, you accept these terms.
            </Typography>
          </Box>
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center", // Centers horizontally
            marginTop: "30px",
            marginBottom: "-20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              // margin: "20px",
              gap: "0px",
              flexWrap: "wrap",
              maxWidth: "1500px",
              width: "100%",
              padding: "0 20px",
              minHeight: "550px",
            }}
          >
            <div
              style={{
                // marginTop: "10px",
                // marginLeft: "50px",
                // marginRight: "5px",
                padding: "10px",
                maxWidth: "700px",
                width: "100%",
                position: "relative",
                minHeight: "520px", // Ensure enough space for both views
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "-30px",
                  left: "10px",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    border: "1.5px solid #686868",
                    borderRadius: "2px",
                  }}
                >
                  <button
                    style={{
                      flex: 1,
                      width: "100px",
                      padding: "10px 20px",
                      backgroundColor:
                        selectedMeasurement === "Metric" ? "black" : "white",
                      color:
                        selectedMeasurement === "Metric" ? "white" : "#686868",
                      border: "none",
                      borderRadius: "2px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                    }}
                    onClick={() => setSelectedMeasurement("Metric")}
                  >
                    Metric
                  </button>
                  <button
                    style={{
                      flex: 1,
                      width: "100px",
                      padding: "10px 20px",
                      backgroundColor:
                        selectedMeasurement === "Imperial" ? "black" : "white",
                      color:
                        selectedMeasurement === "Imperial"
                          ? "white"
                          : "#686868",
                      border: "none",
                      borderRadius: "2px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                    }}
                    onClick={() => setSelectedMeasurement("Imperial")}
                  >
                    Imperial
                  </button>
                </Box>
              </Box>
              {selectedToggle === "TorqueSpeed" ? (
                <div>
                  <RPMTorqueSelector
                    rpms={rpms}
                    torques={torques}
                    pressures={pressure}
                    flow={flow}
                    motorCounts={motorCounts}
                    onSelectionChange={handleSelection}
                    measurementUnit={selectedMeasurement}
                    filterSelected={filterSelected}
                    selectedToggle={selectedToggle}
                    loader={loader}
                  />
                </div>
              ) : (
                <div>
                  <FlowPressureSelector
                    pressures={pressure}
                    flow={flow}
                    motorCountsOfPressureFlow={motorCounts}
                    onSelectionChange={handleSelectionForFlowAndPressure}
                    measurementUnit={selectedMeasurement}
                    filterSelected={filterSelected}
                    selectedToggle={selectedToggle}
                    loader={loader}
                  />
                </div>
              )}
            </div>
            <div
              style={{
                padding: "10px",
                maxWidth: "700px",
                width: "100%",
                position: "relative",
                minHeight: "520px",
                marginTop: "20px",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "-25px",
                  right: "-20px",
                  zIndex: 1,
                }}
              >
                {/* <Box sx={{ position: "relative" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: "-28px",
                      left: "10",
                      transform: "translateX(-80%)",
                      backgroundColor: "#484848",
                      color: "white",
                      padding: "4px 10px",
                      borderRadius: "2px",
                      // fontWeight: "bold",
                      whiteSpace: "nowrap",
                      // boxShadow: "0 0 3px rgba(0,0,0,0.2)",
                      fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                    }}
                  >
                    {layout === "bar"
                      ? "Note: Switch to table for more data"
                      : "Note: Switch to Bar Graph"}
                  </Typography>
                  {layout === "bar" ? (
                    <TableViewIcon
                      sx={{
                        fontSize: 30,
                        color: "black",
                        cursor: "pointer",
                        marginRight: "50px",
                      }}
                      onClick={() => {
                        setSelectedBarData(null);
                        setIsTransferEnabled(selectedRows.length > 0);
                        setLayout("table");
                      }}
                    />
                  ) : (
                    <BarChartIcon
                      sx={{
                        fontSize: 30,
                        color: "black",
                        cursor: "pointer",
                        marginRight: "50px",
                      }}
                      onClick={() => {
                        setSelectedRows([]);
                        setIsTransferEnabled(!!selectedBarData);
                        setRowSelected(false);
                        setLayout("bar");
                      }}
                    />
                  )}
                </Box> */}
              </Box>

              {/* Actual Content */}
              {layout === "bar" ? (
                <EfficiencyBarChart
                  data={matches}
                  width={700}
                  height={500}
                  onBarClick={handleBarClick}
                  filter={tableFilter}
                  loader={barLoader}
                  error={barError}
                  iError={iBarError}
                  selectedMeasurement={selectedMeasurement}
                  selectedToggle={selectedToggle}
                />
              ) : (
                <TableDataContainer
                  columnDefs={columnDefs}
                  filteredRowData1={tableData}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  orderBy={orderBy}
                  order={order}
                  handleSort={handleSort}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  toggleRow={toggleRow}
                  expandedRows={expandedRows}
                  selectedRows={selectedRows}
                  toggleRowSelection={toggleRowSelection}
                  selectedToggle={selectedToggle}
                  loader={tableLoader}
                  tableFilter={tableFilter}
                  error={barError}
                  iError={iBarError}
                  selectedMeasurement={selectedMeasurement}
                />
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            // marginTop: "0px",
            marginBottom: "10px",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            maxWidth="1480px" // Keep layout aligned and centered
            padding="0 40px"
            cursor="pointer" // Optional inner padding
          >
            <Box
              sx={{
                position: "relative",
                border: "1.5px solid #686868",
                borderRadius: "2px",
              }}
            >
              <button
                onClick={() => setSelectedToggle("TorqueSpeed")}
                style={{
                  padding: "10px 20px",
                  minWidth: "100px",
                  backgroundColor:
                    selectedToggle === "TorqueSpeed" ? "black" : "white",
                  color: selectedToggle === "TorqueSpeed" ? "white" : "#686868",
                  border: "none",
                  borderRadius: "2px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                  cursor: "pointer",
                }}
              >
                Torque & Speed
              </button>
              <button
                onClick={() => setSelectedToggle("FlowPressure")}
                style={{
                  padding: "10px 20px",
                  minWidth: "100px",
                  backgroundColor:
                    selectedToggle === "FlowPressure" ? "black" : "white",
                  color:
                    selectedToggle === "FlowPressure" ? "white" : "#686868",
                  border: "none",
                  borderRadius: "2px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                  cursor: "pointer",
                }}
              >
                Pressure & Flow
              </button>
            </Box>

            {/* <Box
              sx={{
                position: "relative",
                // border: "1.5px solid #686868",
                // borderRadius: "2px",
              }}
            >
              <button
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: isTransferEnabled
                    ? hover
                      ? "#b6000f" // Hover color
                      : "#e2000f" // Default enabled color
                    : "#bfbebe", // Disabled color
                  color: isTransferEnabled ? "#fff" : "#fff",
                  border: "none",
                  borderRadius: "2px",
                  cursor: isTransferEnabled ? "pointer" : "not-allowed",
                  fontWeight: "bold",
                  fontFamily: `"Myriad Pro", "Source Sans Pro", system-ui, sans-serif`,
                }}
                disabled={!isTransferEnabled}
                onClick={handleTransferClick}
              >
                Transfer Motor to Quick Selector →
              </button>
            </Box> */}
          </Box>
        </div>

        <div>
          <FilterDescription
            onFilterChange={handleFilterChange}
            bearingCapacityValues={bearingCapacityValues}
            measurementUnit={selectedMeasurement}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center", // centers horizontally
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start", // content alignment inside
              padding: "10px",
              maxWidth: "1500px", // wider container for both plot + table
              width: "100%",
              minHeight: "20px",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <EfficiencyMatrixPlot
              selectedMotors={selectedRows}
              onlyRender={userSelectedFromCheckbox}
              measurementUnit={selectedMeasurement}
            />
          </div>
        </div>
        {/* <div
        style={{
          display: "flex",
          justifyContent: "center", // Center horizontally
          alignItems: "center", // Center vertically
          minHeight: "40vh", // Full screen height
          paddingTop: "40px", // Push down if you have header
          boxSizing: "border-box",
          overflow: "auto", // Prevent overflow issues
        }}
      >
        <ImperialEfficiencyMatrixPlot
          selectedMotors={selectedRows}
          onlyRender={userSelectedFromCheckbox}
          measurementUnit={selectedMeasurement}
        />
      </div>  */}

        <div style={{ marginTop: "50px" }}>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default ScatterGraph;
